import { useAntdTable, useRequest } from 'ahooks'
import { Alert, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Steps } from 'antd'
import { ButtonType } from 'antd/es/button/'
import { useAtom } from 'jotai'
import { isNil, omit } from 'lodash'
import moment from 'moment'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { UsersSearch } from '@/components'
import { NormalResourceTable, deleteChildren } from '@/modules/portfolio/portfolio-list'
import { userInfoAtom } from '@/modules/user'
import { FetchType, PpRelation, ResourceType } from '@/schemas'
import { removeHtmlTag } from '@/utils/remove-html-tag'

import { Customer, CustomerFlowStatus, CustomerRole, CustomerWaitForValidateReason } from '../customer.schema'

interface ActionButtonBase {
  customer: Customer
  refresh: () => void
  financialOperators: string[]
  admins: string[]
  buttonType?: ButtonType
}

export const FlowStatusButton: React.FC<ActionButtonBase> = ({
  customer,
  refresh,
  admins,
  financialOperators,
  buttonType = 'link',
}) => {
  switch (customer.flowStatus) {
    case CustomerFlowStatus.待研发审批:
      return (
        <DevWaitForAuditActionButton
          admins={admins}
          financialOperators={financialOperators}
          customer={customer}
          refresh={refresh}
          buttonType={buttonType}
        />
      )
    case CustomerFlowStatus.研发未开始:
      return (
        <DevNotStartedActionButton
          admins={admins}
          financialOperators={financialOperators}
          customer={customer}
          refresh={refresh}
          buttonType={buttonType}
        />
      )
    case CustomerFlowStatus.研发进行中:
      return (
        <>
          <DevOnGoingActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          />
          <ProjectFailedActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          />
        </>
      )
    case CustomerFlowStatus.研发暂停中:
      return (
        <>
          <DevPendingActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          />
          <ProjectFailedActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          />
        </>
      )
    case CustomerFlowStatus.待客户验收:
      return (
        <>
          {!customer.expectValidationTime && (
            <ExpectValidationTimeButton
              admins={admins}
              financialOperators={financialOperators}
              customer={customer}
              refresh={refresh}
              buttonType={buttonType}
            />
          )}
          <CustomerWaitForValidateActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          />
          <ProjectFailedActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          />
        </>
      )
    case CustomerFlowStatus.结果追踪中:
      return (
        <>
          <ResultTrackingActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          />
          <ProjectFailedActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          />
        </>
      )
    case CustomerFlowStatus.结果复核中:
      return (
        <>
          <ResultReviewingActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          />
          {/* <ProjectFailedActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          /> */}
        </>
      )
    case CustomerFlowStatus.待项目结项:
      return (
        <>
          <ProjectWaitForFinishActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          />
          {/* <ProjectFailedActionButton
            admins={admins}
            financialOperators={financialOperators}
            customer={customer}
            refresh={refresh}
            buttonType={buttonType}
          /> */}
        </>
      )
    default:
      return null
  }
}

export const ProjectFailedActionButton: React.FC<ActionButtonBase> = ({ admins, customer, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const [form] = Form.useForm()

  const [visible, setVisible] = React.useState(false)
  const [step, setStep] = React.useState(0)

  const isAdmin = admins.includes(viewer.email) || viewer.isSuperAdmin
  const isOwner = (customer.roles || []).find((r) => r.role === CustomerRole.负责人)?.email === viewer.email
  const isSalesOwner = (customer.roles || []).find((r) => r.role === CustomerRole.销售Owner)?.email === viewer.email

  if (!isAdmin && !isOwner && !isSalesOwner) {
    return null
  }

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/customer/flow/action`,
      data: {
        selfId: customer.selfId,
        flowStatus: customer.flowStatus,
        action: 'fail',
        globalValues: data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(false)
      },
      onSuccess() {
        refresh()
        setVisible(false)
      },
    }
  )

  return (
    <>
      <Button loading={loading} danger onClick={() => setVisible(true)} type={buttonType} size='small'>
        {t('customer.flowStatus.ProjectFailed.button')}
      </Button>
      <Modal
        width={1000}
        title={
          <Steps current={step} onChange={(step) => setStep(step)} className='pr-4'>
            <Steps.Step title={t('final.step.warning.title')} />
            <Steps.Step title={t('customer.flowStatus.ProjectFailed.button')} />
          </Steps>
        }
        onOk={async () => {
          if (step === 0) {
            setStep(1)
          } else {
            const values = await form.validateFields()
            run(values)
          }
        }}
        onCancel={() => {
          setVisible(false)
        }}
        okButtonProps={{ loading, danger: step === 1 }}
        cancelButtonProps={{
          loading,
          disabled: step === 0,
          onClick: () => {
            if (step === 1) {
              setStep(0)
            }
          },
        }}
        okText={step === 0 ? t('next.step') : t('confirm')}
        cancelText={t('previous.step')}
        visible={visible}
      >
        {step === 0 ? (
          <FinalActionResourceTable
            actionStatus={t('customer.flowStatus.termination.failed')}
            customer={customer}
            refresh={refresh}
          />
        ) : null}
        {step === 1 ? (
          <div>
            <p className='text-red-500'>{t('customer.flowStatus.ProjectFailed.warning.message')}</p>
            <Form form={form} layout='vertical'>
              <Form.Item
                rules={[{ whitespace: true, required: true }]}
                name='reason'
                label={t('customer.flowStatus.ProjectFailed.reason')}
              >
                <Input.TextArea autoSize={{ minRows: 3 }} />
              </Form.Item>
            </Form>
          </div>
        ) : null}
      </Modal>
    </>
  )
}

export const DevWaitForAuditActionButton: React.FC<ActionButtonBase> = ({ customer, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [rejectForm] = Form.useForm()
  const [viewer] = useAtom(userInfoAtom)
  const [visible, setVisible] = React.useState(false)
  const [rejectModalVisible, setRejectModalVisible] = React.useState(false)

  const isCommittee = viewer.roles.find((r) => r.role === `${ResourceType.CUSTOMER}_committee`)
  const isAdmin = viewer.isSuperAdmin
  const shouldDisabled = !isCommittee && !isAdmin

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/customer/flow/action`,
      data: {
        selfId: customer.selfId,
        flowStatus: customer.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(false)
        setRejectModalVisible(false)
      },
      onSuccess() {
        refresh()
        setVisible(false)
        setRejectModalVisible(false)
      },
    }
  )

  if (shouldDisabled) {
    return null
  }

  return (
    <>
      <Button loading={loading} type={buttonType} size='small' onClick={() => setVisible(true)}>
        {t('customer.flowStatus.DevWaitForAudit.button')}
      </Button>
      <Modal
        zIndex={10}
        visible={visible}
        title={t('customer.flowStatus.DevWaitForAudit.button')}
        destroyOnClose
        onCancel={() => setVisible(false)}
        footer={
          <div className='flex justify-end gap-2'>
            <Button danger loading={loading} onClick={() => setRejectModalVisible(true)}>
              {t('customer.flowStatus.DevWaitForAudit.reject')}
            </Button>
            <Button
              type='primary'
              loading={loading}
              onClick={async () => {
                const values = await form.validateFields()
                run({ ...values, action: 'confirm' })
              }}
            >
              {t('customer.review.finished')}
            </Button>
          </div>
        }
      >
        <Form form={form} layout='vertical'>
          <Form.Item rules={[{ required: true }]} label={t('customer.TO')} name='TO'>
            <UsersSearch placeholder={t('customer.负责人')} />
          </Form.Item>
          <Form.Item
            rules={[{ required: true }]}
            label={t('customer.projectManager.title')}
            name='projectManager'
            tooltip={t('customer.flowStatus.DevWaitForAudit.form.projectManager.tooltip')}
          >
            <UsersSearch placeholder={t('customer.负责人')} />
          </Form.Item>
          <Form.Item
            label={t('customer.detail.reviewComment')}
            name='reviewComment'
            rules={[{ required: false, whitespace: false }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
      {/*驳回 Modal*/}
      <Modal
        zIndex={100}
        visible={rejectModalVisible}
        okButtonProps={{ loading }}
        cancelButtonProps={{ loading }}
        onCancel={() => setRejectModalVisible(false)}
        onOk={async () => {
          const values = await rejectForm.validateFields()
          run({ action: 'cancel', ...values })
        }}
        title={t('customer.flowStatus.DevWaitForAudit.reject')}
      >
        <Form form={rejectForm} layout='vertical'>
          <Form.Item
            label={t('customer.detail.reviewComment')}
            name='reviewComment'
            rules={[{ required: true, whitespace: false }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export const DevNotStartedActionButton: React.FC<ActionButtonBase> = ({ customer, refresh, buttonType }) => {
  const MaxSubTOCount = 5 as const
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const [visible, setVisible] = React.useState(false)
  const [form] = Form.useForm()

  // 权限：项目经理
  const projectManagers = (customer.roles || []).filter((r) => r.role === CustomerRole.项目经理).map((r) => r.email)
  const isAdmin = viewer.isSuperAdmin
  const shouldDisabled = !projectManagers.includes(viewer.email) && !isAdmin

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/customer/flow/action`,
      data: {
        selfId: customer.selfId,
        flowStatus: customer.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(false)
      },
      onSuccess() {
        refresh()
        setVisible(false)
      },
    }
  )

  if (shouldDisabled) {
    return null
  }

  return (
    <>
      <Button loading={loading} type={buttonType} size='small' onClick={() => setVisible(true)}>
        {t('customer.flowStatus.DevNotStarted.button')}
      </Button>
      <Modal
        visible={visible}
        destroyOnClose
        onCancel={() => setVisible(false)}
        okText={t('confirm')}
        okButtonProps={{
          loading,
          onClick: async () => {
            const values = await form.validateFields()
            run(values)
          },
        }}
        cancelText={t('cancel')}
        cancelButtonProps={{
          loading,
          onClick: () => {
            setVisible(false)
          },
        }}
      >
        <div className='whitespace-pre-wrap text-red-500 mb-4'>
          {t('customer.flowStatus.DevNotStarted.form.note')}:
          <div>
            <Trans
              i18nKey='customer.flowStatus.DevNotStarted.form.note1'
              components={{
                Bold: <span style={{ fontWeight: 'bold' }} />,
              }}
            ></Trans>
          </div>
        </div>
        <Form
          form={form}
          layout='vertical'
          onFieldsChange={(changedFields) => {
            // 只取最新的 5 个
            const subTOs = changedFields.find((c) => c.name[0] === 'subTOs')?.value
            if (subTOs && subTOs.length > MaxSubTOCount) {
              form.setFieldsValue({ subTOs: subTOs.slice(0, MaxSubTOCount) })
            }
          }}
        >
          <Form.Item rules={[{ required: true }]} label={t('customer.predictWorkload')} name='predictWorkload'>
            <InputNumber min={1} precision={0} />
          </Form.Item>
          <Form.Item label={t('customer.子TO')} name='subTOs'>
            <UsersSearch mode='multiple' placeholder={t('customer.子TO')} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export const DevOnGoingActionButton: React.FC<ActionButtonBase> = ({ customer, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const [visible, setVisible] = React.useState(false)
  const [form] = Form.useForm()

  const [deliveryVisible, setDeliveryVisible] = React.useState(false)
  const [deliveryForm] = Form.useForm()

  const TOs = (customer.roles || []).filter((r) => r.role === CustomerRole.TO).map((r) => r.email)
  const isAdmin = viewer.isSuperAdmin
  const shouldDisabled = !TOs.includes(viewer.email) && !isAdmin

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/customer/flow/action`,
      data: {
        selfId: customer.selfId,
        flowStatus: customer.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(false)
      },
      onSuccess() {
        refresh()
        setVisible(false)
      },
    }
  )

  if (shouldDisabled) {
    return null
  }

  return (
    <>
      <Button type={buttonType} size='small' onClick={() => setDeliveryVisible(true)} loading={loading}>
        {t('customer.flowStatus.DevOnGoing.button.delivery')}
      </Button>
      <Button danger loading={loading} type={buttonType} size='small' onClick={() => setVisible(true)}>
        {t('customer.flowStatus.DevOnGoing.button.pending')}
      </Button>
      <Modal
        visible={deliveryVisible}
        destroyOnClose
        onCancel={() => setDeliveryVisible(false)}
        okText={t('confirm')}
        okButtonProps={{
          loading,
          onClick: async () => {
            const values = await deliveryForm.validateFields()
            run({
              ...values,
              percentage: `${values.percentage}%`,
              deliveryTime: values.deliveryTime.format('YYYY-MM-DD'),
              action: 'delivery',
            })
          },
        }}
        cancelText={t('cancel')}
        cancelButtonProps={{
          loading,
          onClick: () => {
            setDeliveryVisible(false)
          },
        }}
      >
        <Form
          form={deliveryForm}
          initialValues={{ deliveryTime: moment(), description: removeHtmlTag(customer.goal) }}
          layout='vertical'
        >
          <Form.Item
            tooltip={t('customer.flowStatus.DevOnGoing.deliveryTime.tooltip')}
            rules={[{ required: true }]}
            label={t('customer.detail.deliveryTime')}
            name='deliveryTime'
          >
            <DatePicker disabledDate={(current) => current.isAfter(moment(), 'day')} showTime={false} picker='date' />
          </Form.Item>
          <Form.Item
            rules={[{ required: true }]}
            label={t('customer.flowStatus.DevOnGoing.percentage.title')}
            name='percentage'
          >
            <InputNumber
              min={1}
              max={100}
              precision={0}
              formatter={(value) => `${value}%`}
              parser={(value) => value!.replace('%', '') as any}
            />
          </Form.Item>
          <Form.Item
            tooltip={t('customer.flowStatus.DevOnGoing.description.tooltip')}
            label={t('customer.flowStatus.DevOnGoing.description.title')}
            name='description'
          >
            <Input.TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        visible={visible}
        destroyOnClose
        onCancel={() => setVisible(false)}
        okText={t('confirm')}
        okButtonProps={{
          loading,
          onClick: async () => {
            const values = await form.validateFields()
            run({ ...values, action: 'pending' })
          },
        }}
        cancelText={t('cancel')}
        cancelButtonProps={{
          loading,
          onClick: () => {
            setVisible(false)
          },
        }}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            rules={[{ whitespace: true, required: true }]}
            label={t('customer.flowStatus.DevOnGoing.pending.reason')}
            name='reason'
          >
            <Input.TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export const DevPendingActionButton: React.FC<ActionButtonBase> = ({ admins, customer, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [visible, setVisible] = React.useState(false)
  const [viewer] = useAtom(userInfoAtom)
  const [step, setStep] = React.useState(0)

  const [form] = Form.useForm()

  const roles = customer.roles || []
  const TOs = roles.filter((r) => r.role === CustomerRole.TO).map((r) => r.email)

  const isAdmin = admins.includes(viewer.email) || viewer.isSuperAdmin
  const shouldDisplayContinueButton = TOs.includes(viewer.email) || viewer.isSuperAdmin
  const shouldDisplayCancelButton = isAdmin

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/customer/flow/action`,
      data: {
        selfId: customer.selfId,
        flowStatus: customer.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(false)
      },
      onSuccess() {
        refresh()
        setVisible(false)
      },
    }
  )

  return (
    <>
      {shouldDisplayContinueButton && (
        <Button loading={loading} type={buttonType} size='small' onClick={() => run({ action: 'continue' })}>
          {t('customer.flowStatus.DevPending.button.continue')}
        </Button>
      )}
      {shouldDisplayCancelButton && (
        <Button danger onClick={() => setVisible(true)} loading={loading} type={buttonType} size='small'>
          {t('customer.flowStatus.DevPending.button.cancel')}
        </Button>
      )}
      <Modal
        visible={visible}
        closable
        width={1000}
        title={
          <Steps current={step} onChange={(step) => setStep(step)} className='pr-4'>
            <Steps.Step title={t('final.step.warning.title')} />
            <Steps.Step title={t('customer.flowStatus.DevPending.button.cancel')} />
          </Steps>
        }
        destroyOnClose
        onOk={async () => {
          if (step === 0) {
            setStep(1)
          } else {
            const values = await form.validateFields()
            run({ ...values, action: 'cancel' })
          }
        }}
        onCancel={() => {
          setVisible(false)
        }}
        okText={step === 0 ? t('next.step') : t('confirm')}
        cancelText={t('previous.step')}
        okButtonProps={{ loading, danger: step === 1 }}
        cancelButtonProps={{
          loading,
          disabled: step === 0,
          onClick: () => {
            if (step === 1) {
              setStep(0)
            }
          },
        }}
      >
        {step === 0 ? (
          <FinalActionResourceTable
            actionStatus={t('customer.flowStatus.termination.cancel')}
            customer={customer}
            refresh={refresh}
          />
        ) : null}
        {step === 1 ? (
          <Form form={form} layout='vertical'>
            <Form.Item
              rules={[{ whitespace: true, required: true }]}
              label={t('customer.flowStatus.DevPending.cancel.reason')}
              name='reason'
            >
              <Input.TextArea autoSize={{ minRows: 3 }} />
            </Form.Item>
          </Form>
        ) : null}
      </Modal>
    </>
  )
}

export const CustomerWaitForValidateActionButton: React.FC<ActionButtonBase> = ({ customer, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const [visible, setVisible] = React.useState<'pass' | 'reject' | undefined>()
  const [form] = Form.useForm()
  const [delayReason, setDelayReason] = React.useState<CustomerWaitForValidateReason | undefined>()

  const serviceOwners = (customer.roles || []).filter((r) => r.role === CustomerRole.服务Owner).map((r) => r.email)
  const isAdmin = viewer.isSuperAdmin
  const shouldDisabled = !serviceOwners.includes(viewer.email) && !isAdmin

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/customer/flow/action`,
      data: {
        selfId: customer.selfId,
        flowStatus: customer.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(undefined)
      },
      onSuccess() {
        refresh()
        setVisible(undefined)
      },
    }
  )

  // 获取研发交付 - 待客户验收状态时间
  const deliveryTime = customer.deliveryTime
  const today = moment().utc()
  const isExceedMaxAllowedDay = deliveryTime
    ? today.clone().subtract(2, 'week').isAfter(moment(deliveryTime).utc(), 'day')
    : true

  if (!deliveryTime) {
    return <Alert message={t('customer.flowStatus.CustomerWaitForValidate.warning')} type='warning' showIcon closable />
  }

  if (shouldDisabled) {
    return null
  }

  return (
    <>
      <Button loading={loading} type={buttonType} size='small' onClick={() => setVisible('pass')}>
        {t('customer.flowStatus.CustomerWaitForValidate.pass.button')}
      </Button>
      <Button danger loading={loading} type={buttonType} size='small' onClick={() => setVisible('reject')}>
        {t('customer.flowStatus.CustomerWaitForValidate.reject.button')}
      </Button>
      <Modal
        visible={!!visible}
        destroyOnClose
        onCancel={() => setVisible(undefined)}
        okText={t(visible === 'pass' ? 'confirm' : 'reject')}
        okButtonProps={{
          loading,
          danger: visible === 'reject',
          onClick: async () => {
            const values = await form.validateFields()
            const actualReleaseTime = visible === 'pass' ? values.actualReleaseTime.format('YYYY-MM-DD') : undefined
            run({ action: visible, ...values, actualReleaseTime })
          },
        }}
        cancelText={t('cancel')}
        cancelButtonProps={{
          loading,
          onClick: () => {
            setVisible(undefined)
          },
        }}
      >
        {visible === 'pass' && (
          <div className='my-4'>
            <Alert
              message={t('customer.flowStatus.CustomerWaitForValidate.message')}
              type={isExceedMaxAllowedDay ? 'error' : 'warning'}
            />
          </div>
        )}

        <Form
          form={form}
          initialValues={{ ...(visible === 'pass' ? { actualReleaseTime: today } : {}) }}
          layout='vertical'
          onFieldsChange={(changedFields) => {
            const [field] = changedFields
            if (Object.values(CustomerWaitForValidateReason).includes(field?.value)) {
              setDelayReason(field.value)
            }
          }}
        >
          {visible === 'pass' && (
            <>
              <Form.Item
                name='actualReleaseTime'
                tooltip={t('customer.flowStatus.CustomerWaitForValidate.actualReleaseTime.tooltip')}
                label={t('customer.flowStatus.CustomerWaitForValidate.actualReleaseTime.title')}
              >
                <DatePicker disabledDate={(current) => current.isAfter(moment(), 'day')} picker='date' />
              </Form.Item>

              {isExceedMaxAllowedDay && (
                <>
                  <Form.Item
                    required
                    name='delayReason'
                    label={t('customer.flowStatus.CustomerWaitForValidate.selectReason.title')}
                  >
                    <Select
                      options={Object.keys(CustomerWaitForValidateReason).map((value) => ({
                        value,
                        label: t(`customer.flowStatus.CustomerWaitForValidate.selectReason.${value}`),
                      }))}
                    ></Select>
                  </Form.Item>
                  {delayReason && (
                    <Form.Item
                      rules={[{ whitespace: true, required: true }]}
                      label={t('customer.flowStatus.CustomerWaitForValidate.exceedReason')}
                      name='comment'
                    >
                      <Input.TextArea autoSize={{ minRows: 3 }} />
                    </Form.Item>
                  )}
                </>
              )}
            </>
          )}
          {visible === 'reject' && (
            <Form.Item
              name='reason'
              rules={[{ whitespace: true, required: true }]}
              label={t('customer.flowStatus.CustomerWaitForValidate.rejectReason')}
            >
              <Input.TextArea autoSize={{ minRows: 3 }} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  )
}

export const ResultTrackingActionButton: React.FC<ActionButtonBase> = ({ admins, customer, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const [visible, setVisible] = React.useState<'release' | 'cancel'>()
  const [step, setStep] = React.useState(0)
  const [form] = Form.useForm()

  const salesOwners = (customer.roles || []).filter((r) => r.role === CustomerRole.销售Owner).map((r) => r.email)
  const owners = (customer.roles || []).filter((r) => r.role === CustomerRole.负责人).map((r) => r.email)

  const isAdmin = admins.includes(viewer.email) || viewer.isSuperAdmin
  const shouldDisplayReleaseButton = salesOwners.includes(viewer.email) || owners.includes(viewer.email) || isAdmin
  const shouldDisplayCancelButton = isAdmin

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/customer/flow/action`,
      data: {
        selfId: customer.selfId,
        flowStatus: customer.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(undefined)
      },
      onSuccess() {
        refresh()
        setVisible(undefined)
      },
    }
  )

  React.useEffect(() => {
    form.resetFields()
    if (visible === 'release') {
      form.setFieldsValue({ percentage: 100 })
    }
  }, [visible])

  const isCancel = visible === 'cancel'
  return (
    <>
      {shouldDisplayReleaseButton && (
        <Button loading={loading} type={buttonType} size='small' onClick={() => setVisible('release')}>
          {t('customer.flowStatus.ResultTracking.button.pass')}
        </Button>
      )}
      {shouldDisplayCancelButton && (
        <Button danger loading={loading} onClick={() => setVisible('cancel')} type={buttonType} size='small'>
          {t('customer.flowStatus.ResultTracking.button.cancel')}
        </Button>
      )}
      <Modal
        width={1000}
        title={
          <Steps current={step} onChange={(step) => setStep(step)} className='pr-4'>
            <Steps.Step title={t('final.step.warning.title')} />
            <Steps.Step title={t('customer.flowStatus.ResultTracking.button.cancel')} />
          </Steps>
        }
        onOk={async () => {
          if (step === 0) {
            setStep(1)
          } else {
            const values = await form.validateFields()
            run({
              ...values,
              action: visible,
              percentage: !isNil(values.percentage) ? `${values.percentage}%` : undefined,
            })
          }
        }}
        onCancel={() => {
          setVisible(undefined)
        }}
        okButtonProps={{ loading, danger: step === 1 }}
        cancelButtonProps={{
          loading,
          disabled: step === 0,
          onClick: () => {
            if (step === 1) {
              setStep(0)
            }
          },
        }}
        okText={step === 0 ? t('next.step') : t('confirm')}
        cancelText={t('previous.step')}
        visible={visible === 'cancel'}
      >
        {step === 0 ? (
          <FinalActionResourceTable
            actionStatus={t('customer.flowStatus.termination.cancel')}
            customer={customer}
            refresh={refresh}
          />
        ) : null}
        {step === 1 ? (
          <div>
            <Form form={form} layout='vertical'>
              <Form.Item
                rules={[{ whitespace: true, required: isCancel }]}
                label={t('customer.flowStatus.DevPending.cancel.reason')}
                name={'reason'}
              >
                <Input.TextArea autoSize={{ minRows: 3 }} />
              </Form.Item>
            </Form>
          </div>
        ) : null}
      </Modal>

      <Modal
        visible={visible === 'release'}
        destroyOnClose
        onCancel={() => setVisible(undefined)}
        okText={t('confirm')}
        okButtonProps={{
          loading,
          onClick: async () => {
            const values = await form.validateFields()
            run({
              ...values,
              action: visible,
              percentage: !isNil(values.percentage) ? `${values.percentage}%` : undefined,
            })
          },
        }}
        cancelText={t('cancel')}
        cancelButtonProps={{
          loading,
          onClick: async () => {
            setVisible(undefined)
          },
        }}
      >
        {visible === 'release' && (
          <div className='my-4'>
            <Alert message={t('customer.flowStatus.ResultTracking.message')} type={'success'} />
          </div>
        )}

        <Form form={form} layout='vertical'>
          <Form.Item
            rules={[{ whitespace: true, required: false }]}
            label={t('customer.flowStatus.ResultTracking.comment')}
            name={'comment'}
          >
            <Input.TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>

          <Form.Item
            rules={[{ required: true }]}
            label={t('customer.flowStatus.ResultTracking.percentage')}
            name='percentage'
            tooltip={t('customer.flowStatus.ResultTracking.tooltip.percentage')}
          >
            <InputNumber
              defaultValue={100}
              min={0}
              max={100}
              precision={0}
              formatter={(value) => `${value}%`}
              parser={(value) => value!.replace('%', '') as any}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export const ResultReviewingActionButton: React.FC<ActionButtonBase> = ({
  financialOperators,
  customer,
  refresh,
  buttonType,
}) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const isFinancialOperators = financialOperators.includes(viewer.email) || viewer.isSuperAdmin
  const [visible, setVisible] = React.useState(false)
  const [form] = Form.useForm()

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/customer/flow/action`,
      data: {
        selfId: customer.selfId,
        flowStatus: customer.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  if (!isFinancialOperators) {
    return null
  }

  const flow = customer.flows.find((f) => f.status === CustomerFlowStatus.结果复核中)
  const percentage = flow?.additions?.find((f) => f.type === 'percentage')?.value
  const comment = flow?.additions?.find((f) => f.type === 'comment')?.value

  return (
    <>
      <Button loading={loading} type={buttonType} size='small' onClick={() => setVisible(true)}>
        {t('customer.flowStatus.ResultReviewing.button.pass')}
      </Button>
      <Button danger loading={loading} type={buttonType} size='small' onClick={() => run({ action: 'back' })}>
        {t('customer.flowStatus.ResultReviewing.button.reject')}
      </Button>
      <Modal
        visible={visible}
        destroyOnClose
        onCancel={() => setVisible(false)}
        okText={t('confirm')}
        okButtonProps={{
          loading,
          onClick: async () => {
            const values = await form.validateFields()
            run({
              action: 'confirm',
              ...values,
              percentage: !isNil(values.percentage) ? `${values.percentage}%` : undefined,
            })
          },
        }}
        cancelText={t('cancel')}
        cancelButtonProps={{
          loading,
          onClick: async () => {
            setVisible(false)
          },
        }}
      >
        <Form form={form} layout='vertical' initialValues={{ percentage: 100 }}>
          <Form.Item label={t('customer.flowStatus.ResultTracking.comment')}>
            <Input.TextArea readOnly={true} defaultValue={comment} />
          </Form.Item>
          <Form.Item label={t('customer.flowStatus.ResultTracking.percentage')}>
            <InputNumber
              readOnly={true}
              min={0}
              max={100}
              precision={0}
              formatter={(value) => `${value}%`}
              parser={(value) => value!.replace('%', '') as any}
              defaultValue={percentage ? parseInt(percentage) : undefined}
            />
          </Form.Item>
          <Form.Item
            rules={[{ whitespace: true, required: false }]}
            label={t('customer.flowStatus.ResultReviewing.comment')}
            name='comment'
          >
            <Input.TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>
          <Form.Item
            rules={[{ required: true }]}
            label={t('customer.flowStatus.ResultReviewing.percentage')}
            name='percentage'
            tooltip={t('customer.flowStatus.ResultTracking.tooltip.percentage')}
          >
            <InputNumber
              defaultValue={100}
              min={0}
              max={100}
              precision={0}
              formatter={(value) => `${value}%`}
              parser={(value) => value!.replace('%', '') as any}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export const ProjectWaitForFinishActionButton: React.FC<ActionButtonBase> = ({
  admins,
  customer,
  buttonType,
  refresh,
}) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)

  const [visible, setVisible] = React.useState(false)

  const salesOwners = (customer.roles || []).filter((r) => r.role === CustomerRole.销售Owner).map((r) => r.email)
  const owners = (customer.roles || []).filter((r) => r.role === CustomerRole.负责人).map((r) => r.email)

  const isAdmin = admins.includes(viewer.email) || viewer.isSuperAdmin
  const shouldDisabled = !salesOwners.includes(viewer.email) && !isAdmin && !owners.includes(viewer.email)

  const { run, loading } = useRequest(
    () => ({
      url: '/api/customer/flow/action',
      method: 'post',
      data: {
        selfId: customer.selfId,
        flowStatus: customer.flowStatus,
      },
    }),
    {
      manual: true,
      onSuccess: async () => {
        setVisible(false)
        refresh()
      },
    }
  )

  if (shouldDisabled) {
    return null
  }

  return (
    <>
      <Button loading={loading} onClick={() => setVisible(true)} type={buttonType} size='small'>
        {t('customer.flowStatus.ProjectWaitForFinish.button')}
      </Button>
      <Modal
        width={1000}
        title={t('customer.flowStatus.ProjectWaitForFinish.button')}
        okText={t('customer.flowStatus.ProjectWaitForFinish.popover.button')}
        okButtonProps={{ loading, danger: true }}
        cancelButtonProps={{ disabled: loading }}
        onOk={run}
        onCancel={() => setVisible(false)}
        visible={visible}
      >
        <FinalActionResourceTable
          customer={customer}
          refresh={refresh}
          actionStatus={t('customer.flowStatus.termination.finish')}
        />
      </Modal>
    </>
  )
}

const ExpectValidationTimeButton: React.FC<ActionButtonBase> = ({ customer, refresh, buttonType }) => {
  const { t } = useTranslation()

  const [viewer] = useAtom(userInfoAtom)
  const [expectValidationTimeVisible, setExpectValidationTimeVisible] = React.useState(false)
  const [form] = Form.useForm()

  const roles = customer.roles || []
  const serviceOwner = roles.filter((r) => r.role === CustomerRole.服务Owner).map((r) => r.email)
  const isServiceOwner = serviceOwner.includes(viewer.email)
  const isAdmin = viewer.isSuperAdmin
  const shouldDisabled = !isServiceOwner && !isAdmin

  const { run, loading } = useRequest(
    (data) => ({
      url: '/api/customer/action/expect-validation-time',
      method: 'post',
      data: {
        selfId: customer.selfId,
        expectValidationTime: data.expectValidationTime,
      },
    }),
    {
      manual: true,
      onError() {
        setExpectValidationTimeVisible(false)
      },
      onSuccess() {
        refresh()
        setExpectValidationTimeVisible(false)
        form.resetFields()
      },
    }
  )

  const handleExpectValidationTime = async () => {
    const values = await form.validateFields()
    run({
      expectValidationTime: values.expectValidationTime.format('YYYY-MM-DD'),
    })
  }

  const disabledDate = (current: moment.Moment) => {
    return current && current < moment().startOf('day')
  }

  if (shouldDisabled) {
    return null
  }

  return (
    <>
      <Button type={buttonType} size='small' onClick={() => setExpectValidationTimeVisible(true)}>
        {t('customer.flowStatus.CustomerWaitForValidate.expectValidationTime.button')}
      </Button>

      <Modal
        title={t('customer.flowStatus.CustomerWaitForValidate.expectValidationTime.title')}
        visible={expectValidationTimeVisible}
        onOk={handleExpectValidationTime}
        onCancel={() => {
          setExpectValidationTimeVisible(false)
          form.resetFields()
        }}
        confirmLoading={loading}
      >
        <Form
          form={form}
          initialValues={{
            expectValidationTime: customer.expectValidationTime ? moment(customer.expectValidationTime) : moment(),
          }}
        >
          <Form.Item
            name='expectValidationTime'
            label={t('customer.flowStatus.CustomerWaitForValidate.expectValidationTime.label')}
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: '100%' }} disabledDate={disabledDate} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

const FinalActionResourceTable = ({
  customer,
  refresh,
  actionStatus,
}: {
  customer: Customer
  refresh: () => void
  actionStatus: string
}) => {
  const { t } = useTranslation()

  const { tableProps } = useAntdTable<PpRelation[], any, PpRelation[]>(
    {
      url: '/api/relations/getPpRelations',
      params: {
        parentSelfId: customer.selfId,
        rootTypes: [ResourceType.PROJECT, ResourceType.PORTFOLIO],
        childTypes: [ResourceType.PROJECT, ResourceType.PORTFOLIO],
        fetchType: FetchType.fetchChild,
      },
    },
    {
      refreshDeps: [customer.selfId],
      formatResult: (data) => ({ list: deleteChildren(data), total: 0 }),
    }
  )

  return (
    <>
      <div className='text-red-500 mb-4'>
        <div>{t('customer.flowStatus.ProjectFailed.warning1')}</div>
        <div>{t('customer.flowStatus.ProjectFailed.warning2')}</div>
        <div>{t('customer.flowStatus.ProjectFailed.warning3', { status: actionStatus })}</div>
      </div>
      <div className='max-h-[500px] overflow-y-auto'>
        <NormalResourceTable
          selfId={customer.selfId}
          type={ResourceType.CUSTOMER}
          pageType={ResourceType.CUSTOMER}
          name={document.title}
          onSuccess={refresh}
          canDelete={false}
          canUpdate={false}
          {...omit(tableProps, 'pagination')}
          sticky={true}
        />
      </div>
    </>
  )
}
